import type { Guide } from "./guide";

/** Guide 3: approvals and questions, answer, and money that moves only on a yes. */
export const APPROVALS: Guide = {
  slug: "approvals",
  samples: "approvals",
  title: "Let your user say yes",
  nav: "Your user's yes",
  summary:
    "Bash asks before it spends money or acts for someone: show the question in your app, and answer with your user's choice.",
  minutes: 10,
  lede: "Bash asks before anything that spends money or speaks for someone. The question reaches you, your app shows it to your user, and only their yes moves money.",
  lines: ["answer", "ask", "embed", "events"],
  steps: [
    {
      id: "choose",
      title: "Choose what waits",
      body: [
        "Your file lists what Bash asks the person before doing ([`deploy`](/developers/deploy)); every tool of your MCP servers waits for a yes too, unless your server marks it read-only or your file lets it run without asking, and so does every function your code answers, unless your file marks it `read_only` or `run_without_asking` ([your tools](/developers/guides/your-tools)). A system approval enforces what `approvals` lists for your own functions and your MCP servers' tools that wait for a yes; in a browser or a connected app, Bash is instructed to ask.",
      ],
      file: "approvals.yaml",
      then: "Bash asks before any of these, for every user.",
    },
    {
      id: "see",
      title: "See what Bash asks",
      body: [
        "Bash sends `interaction.created`, and waits. Show your user its `summary`, which names each input of the call, so one of your own tools that takes the price shows it (its `moves_money` is `null`, as only your code knows what your tools cost).",
      ],
      doors: {
        ts: { sample: "guide-approvals-see" },
        py: { sample: "guide-approvals-see" },
        cli: { sample: "guide-approvals-see" },
        http: { sample: "guide-approvals-see" },
        mcp: {
          note: "An agent sees what Bash waits on in the `ask` tool's answer, and answers with the `answer` tool.",
        },
      },
      then: "The conversation shows `waiting`, and nothing is booked or paid.",
    },
    {
      id: "answer",
      title: "Answer with their choice",
      body: [
        "A yes moves money only with `present_user: true`: the person who saw it said yes. The yes to a company action (a tool of your MCP servers, or a function of yours that is not read-only or run without asking) needs it too, sent for the person who saw it.",
      ],
      doors: {
        ts: { sample: "guide-approvals-answer" },
        py: { sample: "guide-approvals-answer" },
        cli: { sample: "guide-approvals-answer" },
        http: { sample: "guide-approvals-answer" },
        mcp: { sample: "answer" },
      },
      then: "On a yes, Bash books and replies. On a no, nothing is booked or paid.",
    },
    {
      id: "questions",
      title: "Answer questions and hand-offs",
      body: [
        "One route answers every kind: `answers` for a question, `done` for a hand-off ([`answer`](/developers/answer)).",
      ],
      doors: {
        ts: { sample: "guide-approvals-question" },
        py: { sample: "guide-approvals-question" },
        cli: { sample: "guide-approvals-question" },
        http: { sample: "guide-approvals-question" },
        mcp: { sample: "guide-approvals-question" },
      },
      then: "Bash carries on with what you said. A body that does not fit is refused as `answer_mismatch`.",
    },
    {
      id: "inside",
      title: "Or ask inside your app",
      body: [
        "Bash inside your app shows its approvals to your user itself ([`embed`](/developers/embed)).",
      ],
      then: "Your webhooks still hear each `interaction.created`.",
    },
    {
      id: "whole",
      title: "The whole thing",
      body: ["Approvals, questions and hand-offs, answered in one loop."],
      doors: {
        ts: { sample: "guide-approvals-whole" },
        py: { sample: "guide-approvals-whole" },
        cli: { sample: "guide-approvals-whole" },
        http: { sample: "guide-approvals-whole" },
        mcp: {
          note: "An agent answers each with the `answer` tool, as the steps show.",
        },
      },
      then: "Bash waits at each, and carries on with your user's answer.",
    },
  ],
};
